import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useDeviceSelectors } from 'react-device-detect';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import AuthorizedUserDTO from '../../../dto/AuthorizedUserDTO';
import { processFormError } from '../../../utils/processFormError';
import {
  SUPPORT_MAIL, SUPPORT_PHONE,
} from '../../../configs';
import Loader from '../../../components/base/Loader/Loader';
import FieldDTO from '../../../components/fields/Abstract/FieldDTO';
import { Alert, Button, Icons } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import { authorizedCheck, authorizedLogin, forgotPassword } from '../actions';
import { AUTHORIZED_MODULE } from '../../../configs/moduleNames';
import TextInputWithLabel from '../../../components/fields/TextInput/TextInputWithLabel';
import TextPasswordInput from '../../../components/fields/TextInput/TextPasswordInput';
import notificationDecorator from '../../Notifications/notificationDecorator';
import { responseLogWithNotification } from '../../../utils/utils';
import { calculateBaseRoute } from '../../../utils/routeByRoles';
import { startLoadApp, endLoadApp } from '../../System/actions';
import LanguageSelector from '../../../components/base/LanguageSelector/LanguageSelector';
import CheckBoxInputWithLabel from '../../../components/fields/CheckBoxInput/CheckBoxInputWithLabel';

export default function LoginPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authorized = useSelector(state => state[AUTHORIZED_MODULE]);
  const { loggedIn, loginProcess, loadingForgotPassword } = authorized;
  const [showForgot, setShowForgot] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [selectors] = useDeviceSelectors(window.navigator.userAgent);
  const intl = useIntl();
  const { formatMessage } = intl;
  const { isMobile } = selectors;
  useEffect(() => {
    if (loggedIn) {
      const baseRoute = calculateBaseRoute(true, [], null, isMobile, true);
      navigate(baseRoute || '/');
    }
  }, []);
  const {
    control,
    handleSubmit,
    reset,
    setValue,
  } = useForm({ mode: 'onBlur' });
  const [error, setError] = useState('');

  const handleLogin = values => {
    setError('');
    dispatch(authorizedLogin(values))
      .then(authorizedData => {
        dispatch(startLoadApp());
        setForgotSuccess(false);
        reset();
        const permissions = authorizedData && authorizedData.role && authorizedData.role.permissions ? authorizedData.role.permissions : {};
        const baseRoute = calculateBaseRoute(true, permissions, null, isMobile, true);
        dispatch(authorizedCheck())
          .then(() => {
            navigate(baseRoute || '/');
            setTimeout(() => { dispatch(endLoadApp()); }, 800);
          })
          .catch(responseLogWithNotification);
      })
      .catch(response => {
        setForgotSuccess(false);
        // responseLogWithNotification(response, formatMessage({ id: 'app.login' }), formatMessage({ id: 'error.default' }));
        setError(typeof response === 'string' ? response : '');
      });
  };

  const handleForgotPassword = values => {
    setError('');
    dispatch(forgotPassword(values))
      .then(() => {
        reset();
        setForgotSuccess(true);
        setShowForgot(false);
        setTimeout(() => {
          setValue('email', values.email);
        }, 100);
        notificationDecorator({
          title: formatMessage({ id: 'app.password.recovery' }),
          message: formatMessage({ id: 'success' }),
        });
      })
      .catch(response => {
        setForgotSuccess(false);
        responseLogWithNotification(response, formatMessage({ id: 'app.password.recovery' }), formatMessage({ id: 'error.default' }));
        setError(processFormError(response));
      });
  };

  const renderLoginForm = values => {
    const authorizedDTO = new AuthorizedUserDTO();
    const attributes = authorizedDTO.getAttributesByName();
    const showPassword = false;
    return (
      <>
        <div className="header">
          <h2>
            {formatMessage({ id: 'menu.login' })}
          </h2>
        </div>
        <form
          className="login-form"
          noValidate
          onSubmit={handleSubmit(handleLogin.bind(this))}
        >
          {forgotSuccess ? <div className="forgot-password-success">{formatMessage({ id: 'app.password.recovery.success' })}</div> : null}
          <FieldDTO
            control={control}
            type="text"
            component={TextInputWithLabel}
            dtoObject={attributes.login}
            variant="material"
          />
          <FieldDTO
            control={control}
            type={showPassword ? 'text' : 'password'}
            component={TextPasswordInput}
            withLabel
            dtoObject={attributes.password}
            className="password-field"
            variant="material"
          />
          <FieldDTO
            control={control}
            component={CheckBoxInputWithLabel}
            withLabel
            dtoObject={{
              name: 'another.computer',
              type: Boolean,
              validators: [],
              labelKey: 'another.computer',
            }}
            className="another-computer-field"
            variant="material"
          />
          {error && <Alert className="mt-3" variant="danger">{error}</Alert>}
          <Button className="login-button big" type="submit">{formatMessage({ id: 'menu.login' })}</Button>
          {/* <div className="forgot-password" onClick={() => { setShowForgot(true); setError(''); }}> */}
          {/*  {formatMessage({ id: 'forgot.password' })} */}
          {/* </div> */}
        </form>
      </>
    );
  };

  const renderForgotPasswordForm = values => {
    const authorizedDTO = new AuthorizedUserDTO();
    const attributes = authorizedDTO.getAttributesByName();
    return (
      <>
        <div className="header">
          <h2>
            {formatMessage({ id: 'app.password.recovery' })}
          </h2>
        </div>
        <form className="login-form" noValidate onSubmit={handleSubmit(handleForgotPassword.bind(this))}>
          <FieldDTO
            type="text"
            control={control}
            component={TextInputWithLabel}
            dtoObject={attributes.email}
            variant="material"
          />
          {error && <Alert className="mt-3" variant="danger">{error}</Alert>}
          <div className="forgot-button-wrapper">
            <Button className="cancel-button big" variant="secondary" onClick={() => { setShowForgot(false); }}>{formatMessage({ id: 'app.cancel' })}</Button>
            <Button className="login-button big" type="submit">{formatMessage({ id: 'app.send' })}</Button>
          </div>
        </form>
      </>
    );
  };

  return (
    <>
      <Loader loading={loginProcess || loadingForgotPassword} />
      <div className="login-page-wrapper">
        <div className="login-page">
          <div className="form-block-wrapper">
            <div className="form-block">
              <div className="language-selector-block">
                <LanguageSelector />
              </div>
              <div className="login-block">
                {!showForgot ? renderLoginForm() : renderForgotPasswordForm()}
              </div>
              <div className="copyright f5">
                {formatMessage({ id: 'app.support' })}:
                {' '}
                <br />
                {formatMessage({ id: 'app.phone.short' })}.
                {' '}
                {SUPPORT_PHONE}
                <br />
                <a color="inherit" href={`mailto:${SUPPORT_MAIL}`}>
                  {SUPPORT_MAIL}
                </a>
                &nbsp;
                <br />
              </div>
            </div>
          </div>
          <div className="image-block">
            <div className="logo-image-wrapper">
              <div className="logo-image" />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
